import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  User, CreditCard, FileText, Check, LogOut, ArrowLeft, 
  Sparkles, Zap, Crown, Download, RefreshCw, Award, MessageCircle,
  Calendar, Video, Phone, Clock, Shield, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { paymentsApi } from '../services/api';
import { toast } from 'sonner';

// New Premium Packages
const PACKAGES = [
  {
    id: 'flash',
    name: 'Flash',
    subtitle: 'À l\'unité',
    price: 97,
    priceLabel: '€ TTC',
    credits: 1,
    description: 'Idéal pour tester un premier projet',
    features: [
      '1 audit complet',
      'Rapport PDF "Bank-Ready"',
      'Données DVF du quartier',
      'Support email sous 48h'
    ],
    icon: Sparkles,
    popular: false,
    color: 'from-gray-500 to-gray-600'
  },
  {
    id: 'pro',
    name: 'Marchand Pro',
    subtitle: 'Le plus populaire',
    price: 247,
    priceLabel: '€ TTC/mois',
    credits: 4,
    monthly: true,
    description: 'Pour les marchands de biens actifs',
    features: [
      '4 audits par mois',
      'Analyse prioritaire en 12h',
      'Accès DVF illimité',
      'Support prioritaire',
      'Historique des analyses'
    ],
    icon: Zap,
    popular: true,
    color: 'from-gold to-amber-600',
    savings: 'Économisez 40%'
  },
  {
    id: 'master',
    name: 'Master',
    subtitle: 'Accompagnement Premium',
    price: 497,
    priceLabel: '€ TTC/mois',
    credits: -1,
    monthly: true,
    description: 'Suivi personnalisé avec l\'expert',
    features: [
      'Audits illimités',
      '1 session visio 30min/mois',
      'Analyse de vos devis artisans',
      'Chat direct avec l\'expert',
      'Ligne directe WhatsApp',
      'Formation stratégie MDB'
    ],
    icon: Crown,
    popular: false,
    color: 'from-navy to-navy-600',
    badge: 'VIP'
  }
];

const SUBSCRIPTION_LABELS = {
  free: 'Gratuit',
  flash: 'Flash',
  pro: 'Marchand Pro',
  master: 'Master VIP'
};

// Expert Badge Component - variant: "light" (default) or "dark" (for navy background)
const ExpertBadge = ({ className = "", variant = "light" }) => (
  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
    variant === "dark" 
      ? "bg-white/10 border border-white/20" 
      : "bg-navy/5 border border-navy/20"
  } ${className}`}>
    <Award className="w-3.5 h-3.5 text-gold" />
    <span className={`text-xs font-medium ${variant === "dark" ? "text-gold" : "text-navy"}`}>
      25 ans d'expérience bâtiment
    </span>
  </div>
);

export default function AccountPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, logout, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    try {
      const maxAttempts = 5;
      for (let i = 0; i < maxAttempts; i++) {
        const response = await paymentsApi.getStatus(sessionId);
        if (response.data.payment_status === 'paid') {
          toast.success('Paiement confirmé ! Bienvenue dans l\'équipe.');
          await refreshUser();
          // Clear URL params
          navigate('/account', { replace: true });
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      toast.info('Vérification du paiement en cours...');
    } catch (error) {
      console.error('Payment check error:', error);
      toast.error('Erreur lors de la vérification du paiement');
    } finally {
      setCheckingPayment(false);
    }
  };

  const handlePurchase = async (packageId) => {
    setLoading(true);
    try {
      const response = await paymentsApi.createCheckout(packageId);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Erreur lors de la création du paiement');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const isPremium = user?.subscription_type === 'master';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-navy text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-btn"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <h1 className="font-heading text-xl font-bold">Mon Compte</h1>
            </div>
            <div className="flex items-center gap-3">
              <ExpertBadge variant="dark" className="flex" />
              <Button
                data-testid="logout-btn"
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-white/70 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {checkingPayment && (
        <div className="bg-gold text-black py-3 text-center">
          <RefreshCw className="w-4 h-4 inline-block animate-spin mr-2" />
          Vérification du paiement en cours...
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Profile Section */}
        <Card className="mb-8 border-0 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-navy to-navy-700 p-6 text-white">
            <div className="flex items-start gap-6">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-xl font-bold">{user?.email}</h2>
                <p className="text-white/70 mt-1">
                  Membre depuis {formatDate(user?.created_at || new Date().toISOString())}
                </p>
                <div className="flex items-center gap-4 mt-4">
                  <Badge className={`${
                    user?.subscription_type === 'master' ? 'bg-gold text-black' :
                    user?.subscription_type === 'pro' ? 'bg-amber-500 text-black' :
                    'bg-white/20 text-white'
                  }`}>
                    {SUBSCRIPTION_LABELS[user?.subscription_type] || 'Gratuit'}
                  </Badge>
                  {user?.credits !== undefined && user?.credits >= 0 && (
                    <span className="text-sm text-white/70">
                      <strong className="text-white">{user?.credits || 0}</strong> audits restants
                    </span>
                  )}
                  {user?.role === 'expert' && (
                    <Badge className="bg-emerald-500 text-white">Expert</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Premium Features for Master subscribers */}
          {isPremium && (
            <CardContent className="p-6 bg-gradient-to-r from-gold/10 to-amber-50">
              <h3 className="font-heading font-bold text-navy mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-gold" />
                Vos avantages Master
              </h3>
              <div className="grid md:grid-cols-3 gap-4">
                <Button
                  data-testid="book-call-btn"
                  variant="outline"
                  className="h-auto py-4 border-navy/20 hover:bg-navy hover:text-white"
                  onClick={() => window.open('https://calendly.com', '_blank')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span className="font-medium">Réserver ma visio</span>
                    <span className="text-xs text-muted-foreground">30 min ce mois</span>
                  </div>
                </Button>
                <Button
                  data-testid="chat-expert-btn"
                  variant="outline"
                  className="h-auto py-4 border-navy/20 hover:bg-navy hover:text-white"
                  onClick={() => navigate('/dashboard?chat=open')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-medium">Chat Expert</span>
                    <span className="text-xs text-muted-foreground">Réponse &lt; 2h</span>
                  </div>
                </Button>
                <Button
                  data-testid="whatsapp-btn"
                  variant="outline"
                  className="h-auto py-4 border-navy/20 hover:bg-emerald-500 hover:text-white hover:border-emerald-500"
                  onClick={() => window.open('https://wa.me/33600000000', '_blank')}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Phone className="w-5 h-5" />
                    <span className="font-medium">Ligne Directe</span>
                    <span className="text-xs text-muted-foreground">WhatsApp VIP</span>
                  </div>
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Value Proposition + Pricing — masqués pour les clients partenaires */}
        {!user?.partner_subdomain && (
        <>
        <div className="text-center mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-2">
            Choisissez votre niveau d'accompagnement
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            L'application ne vend pas un simple PDF. Vous achetez l'expertise d'un 
            <span className="text-gold font-semibold"> Marchand de Biens avec 25 ans d'expérience </span>
            dans le bâtiment.
          </p>
        </div>

        {/* Pricing Section */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {PACKAGES.map((pkg) => {
            const Icon = pkg.icon;
            const isCurrentPlan = user?.subscription_type === pkg.id;
            
            return (
              <Card 
                key={pkg.id} 
                data-testid={`package-${pkg.id}`}
                className={`relative border-2 transition-all hover:shadow-xl ${
                  pkg.popular 
                    ? 'border-gold shadow-gold/20 scale-105 z-10' 
                    : isCurrentPlan 
                      ? 'border-emerald-500' 
                      : 'border-gray-200'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-gold text-black font-bold px-4 py-1 shadow-lg">
                      <Star className="w-3 h-3 mr-1" />
                      Plus populaire
                    </Badge>
                  </div>
                )}
                {pkg.badge && (
                  <div className="absolute -top-4 right-4">
                    <Badge className="bg-navy text-white font-bold">
                      {pkg.badge}
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-2 pt-6">
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-3 bg-gradient-to-br ${pkg.color} text-white shadow-lg`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  <CardTitle className="text-navy text-xl">{pkg.name}</CardTitle>
                  <CardDescription className="font-medium">{pkg.subtitle}</CardDescription>
                </CardHeader>
                
                <CardContent className="text-center">
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-navy">{pkg.price}</span>
                    <span className="text-muted-foreground text-sm">{pkg.priceLabel}</span>
                    {pkg.savings && (
                      <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">
                        {pkg.savings}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-6">{pkg.description}</p>
                  
                  <ul className="space-y-3 text-sm mb-6 text-left">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {isCurrentPlan ? (
                    <Button disabled className="w-full bg-emerald-100 text-emerald-700">
                      <Check className="w-4 h-4 mr-2" />
                      Plan actuel
                    </Button>
                  ) : (
                    <Button
                      data-testid={`buy-${pkg.id}`}
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={loading}
                      className={`w-full font-semibold ${
                        pkg.popular 
                          ? 'bg-gold hover:bg-gold/90 text-black shadow-lg' 
                          : 'bg-navy hover:bg-navy/90 text-white'
                      }`}
                    >
                      {loading ? 'Chargement...' : pkg.monthly ? 'S\'abonner' : 'Acheter'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Trust Section */}
        <Card className="border-0 shadow-sm bg-white mb-8">
          <CardContent className="p-6">
            <div className="grid md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-navy">25+</div>
                <div className="text-sm text-muted-foreground">Années d'expérience</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gold">500+</div>
                <div className="text-sm text-muted-foreground">Projets analysés</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-500">98%</div>
                <div className="text-sm text-muted-foreground">Clients satisfaits</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-navy">12h</div>
                <div className="text-sm text-muted-foreground">Délai moyen d'analyse</div>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
        )}

        {/* Invoices Section */}
        <Card className="border-0 shadow-sm mb-8">
          <CardHeader>
            <CardTitle className="text-navy flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Historique des factures
            </CardTitle>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p>Aucune facture pour le moment</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-navy">{tx.package_name}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(tx.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-bold text-navy">{tx.amount}€</span>
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </main>
    </div>
  );
}
